<template>
  <div v-if="show" class="ace-import-overlay" @click.self="handleClose">
    <div
      ref="aceImportModal"
      class="ace-import-modal draggable-popup"
      :class="{ 'html-open': isHtmlPanelOpen }"
    >
      <div class="ace-import-header draggable-header">
        <h3>Ace Statements Import</h3>
        <button class="close-btn" @click="handleClose">X</button>
      </div>

      <div v-if="isLoading" class="busy-overlay">
        <div class="spinner"></div>
        <div class="busy-label">Parsing statement...</div>
      </div>

      <div class="ace-import-body">
        <div class="ace-import-main">
          <div class="section-header">1) Select Statement File</div>
          <div class="file-instructions">
            <b>Instructions:</b> Find an email from Great Lakes with the
            statement included as an .html attachment. <br />Save the HTML
            attachment using right-click...Save As... on the attachment to the
            directory<br />
            Z:\var-lib-app\pixie_v2\Data\Ace Statements\ and name it like "May
            2026 statement.html".
          </div>
          <hr />
          <div class="file-list">
            <button
              v-for="file in files"
              :key="file.filename"
              class="file-btn"
              :class="{ selected: selectedFile === file.filename }"
              @click="selectFile(file.filename)"
            >
              {{ file.filename }}
            </button>
            <div v-if="!files.length && !isLoading" class="hint">
              No .html files found in Ace Statements folder.
            </div>
          </div>

          <div v-if="selectedFile" class="section-header">
            2) Review Rows and Class Matches
          </div>

          <div v-if="selectedFile" class="parent-form">
            <div class="form-grid">
              <label>
                Date
                <input type="date" v-model="parent.Date" />
              </label>
              <label>
                Type
                <input type="text" v-model="parent.Type" />
              </label>
              <label>
                Check #
                <input type="text" v-model="parent.Num" />
              </label>
              <label>
                Name
                <input type="text" v-model="parent.Name" />
              </label>
              <label>
                Parent Account
                <input type="text" v-model="parent.Account" />
              </label>
              <label>
                Child Account
                <input type="text" v-model="parent.ChildAccount" />
              </label>
            </div>
            <label class="full-row">
              Parent Memo
              <input type="text" v-model="parent.Memo" />
            </label>
          </div>

          <div v-if="parsedRows.length" class="rows-table-wrap">
            <table class="rows-table">
              <colgroup>
                <col class="col-ref" />
                <col class="col-description" />
                <col class="col-amount" />
                <col class="col-amount" />
                <col class="col-amount" />
                <col class="col-class" />
              </colgroup>
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Description</th>
                  <th class="amount-col">Debit</th>
                  <th class="amount-col">Credit</th>
                  <th class="amount-col">Amount</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in parsedRows"
                  :key="row.ref + '-' + index"
                >
                  <td>{{ row.ref }}</td>
                  <td>{{ row.description }}</td>
                  <td class="amount-col">{{ formatCurrency(row.debit) }}</td>
                  <td class="amount-col">{{ formatCurrency(row.credit) }}</td>
                  <td class="amount-col">{{ formatCurrency(row.amount) }}</td>
                  <td class="class-cell">
                    <input
                      type="text"
                      v-model="row.classQuery"
                      @input="onClassInput(index)"
                      @focus="$event.target.select()"
                      @blur="hideClassMatches(index)"
                      placeholder="Search class"
                      class="class-input"
                    />
                    <div v-if="row.classMatches.length" class="dropdown">
                      <div
                        v-for="match in row.classMatches"
                        :key="match"
                        class="dropdown-item"
                        @mousedown.prevent="selectClass(index, match)"
                      >
                        {{ match }}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td class="total-label" colspan="2">Total</td>
                  <td class="amount-col">
                    {{ formatCurrency(parsedTotals.debit) }}
                  </td>
                  <td class="amount-col">
                    {{ formatCurrency(parsedTotals.credit) }}
                  </td>
                  <td class="amount-col">
                    {{ formatCurrency(parsedTotals.amount) }}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
        </div>

        <button
          class="html-toggle"
          @click="toggleHtmlPanel"
          :title="isHtmlPanelOpen ? 'Hide source HTML' : 'Show source HTML'"
        >
          {{ isHtmlPanelOpen ? '<' : '>' }}
        </button>

        <div v-show="isHtmlPanelOpen" class="source-panel">
          <div class="source-panel-title">Source HTML</div>
          <iframe
            v-if="sourceHtml"
            class="source-html"
            :srcdoc="sourceHtml"
            sandbox=""
            title="Ace statement source preview"
          ></iframe>
          <div v-else class="source-empty">No source loaded.</div>
        </div>
      </div>

      <div class="ace-import-footer">
        <button class="secondary" @click="handleClose">Cancel</button>
        <button
          class="primary"
          :disabled="isSubmitDisabled"
          @click="importStatement"
        >
          {{ isSaving ? 'Importing...' : 'Import Statement' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'AceStatementImport',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      files: [],
      selectedFile: '',
      parsedRows: [],
      classes: [],
      errorMessage: '',
      statementMonth: '',
      sourceHtml: '',
      isHtmlPanelOpen: false,
      parent: {
        Date: new Date().toISOString().split('T')[0],
        Type: 'check',
        Num: '',
        Name: 'Great Lakes Ace',
        Memo: '',
        Account: '10000 : General Checking',
        Split: '-SPLIT-',
        Class: 'Big Property Management',
        ChildAccount: '52000 : Maintenance - General',
        ChildSplit: '10000 : General Checking',
      },
    };
  },
  computed: {
    isSubmitDisabled() {
      if (this.isSaving || !this.selectedFile || !this.parsedRows.length)
        return true;
      return this.parsedRows.some((row) => !row.selectedClass);
    },
    parsedTotals() {
      return this.parsedRows.reduce(
        (totals, row) => {
          totals.debit += Number(row.debit || 0);
          totals.credit += Number(row.credit || 0);
          totals.amount += Number(row.amount || 0);
          return totals;
        },
        { debit: 0, credit: 0, amount: 0 },
      );
    },
  },
  watch: {
    show: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          await this.$nextTick();
          const modal = this.$refs.aceImportModal;
          this.applySavedPosition(modal);
          this.makeDraggable(modal);
          await this.initialize();
        }
      },
    },
  },
  methods: {
    async initialize() {
      this.errorMessage = '';
      this.selectedFile = '';
      this.parsedRows = [];
      this.files = [];
      this.statementMonth = '';
      this.sourceHtml = '';
      this.isHtmlPanelOpen = false;
      this.parent.Date = new Date().toISOString().split('T')[0];
      this.parent.Memo = '';
      await Promise.all([this.loadFiles(), this.loadClasses()]);
    },

    async loadFiles() {
      try {
        this.isLoading = true;
        const response = await axios.get('/api/ace-statements/list');
        this.files = response.data.files || [];
      } catch (error) {
        console.error('Error loading Ace statement files:', error);
        this.errorMessage = 'Could not load Ace statement files.';
      } finally {
        this.isLoading = false;
      }
    },

    async loadClasses() {
      try {
        const response = await axios.get('/api/get-classes');
        this.classes = response.data || [];
      } catch (error) {
        console.error('Error loading classes:', error);
      }
    },

    async selectFile(filename) {
      this.selectedFile = filename;
      this.errorMessage = '';
      this.parsedRows = [];
      this.sourceHtml = '';
      this.isHtmlPanelOpen = true;

      try {
        this.isLoading = true;
        const [parseResponse, sourceResponse] = await Promise.all([
          axios.get('/api/ace-statements/parse', {
            params: { file: filename },
          }),
          axios.get('/api/ace-statements/source-html', {
            params: { file: filename },
          }),
        ]);

        const response = parseResponse;
        this.sourceHtml = sourceResponse.data?.html || '';

        this.statementMonth = response.data.statementMonth || '';
        this.parent.Memo = `Great Lakes Ace ${this.statementMonth || 'Statement'} Account #698759`;

        const rows = response.data.rows || [];
        this.parsedRows = rows.map((row) => {
          const initialClass = row.suggestedClass || '';
          return {
            ...row,
            selectedClass: initialClass,
            classQuery: initialClass,
            classMatches: [],
          };
        });
      } catch (error) {
        console.error('Error parsing Ace statement:', error);
        this.errorMessage = 'Could not parse selected statement.';
      } finally {
        this.isLoading = false;
      }
    },

    onClassInput(index) {
      const row = this.parsedRows[index];
      if (!row) return;

      const query = String(row.classQuery || '')
        .toLowerCase()
        .trim();
      row.selectedClass = '';

      if (!query) {
        row.classMatches = [];
        return;
      }

      const tokens = query
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);

      let matches = this.classes;
      tokens.forEach((token) => {
        matches = matches.filter((item) => item.toLowerCase().includes(token));
      });

      row.classMatches = matches.slice(0, 20);
    },

    hideClassMatches(index) {
      setTimeout(() => {
        if (this.parsedRows[index]) {
          this.parsedRows[index].classMatches = [];
        }
      }, 120);
    },

    toggleHtmlPanel() {
      this.isHtmlPanelOpen = !this.isHtmlPanelOpen;
    },

    applySavedPosition(el) {
      if (!el || !(el instanceof HTMLElement)) return;

      const savedTop = localStorage.getItem('aceImportTop');
      const savedLeft = localStorage.getItem('aceImportLeft');

      if (savedTop && savedLeft) {
        el.style.top = savedTop;
        el.style.left = savedLeft;
        return;
      }

      const top = Math.max((window.innerHeight - el.offsetHeight) / 2, 20);
      const left = Math.max((window.innerWidth - el.offsetWidth) / 2, 20);
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
    },

    makeDraggable(el) {
      if (!el || !(el instanceof HTMLElement)) return;

      let pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      const dragElement = el.querySelector('.draggable-header');

      if (dragElement) {
        dragElement.onmousedown = dragMouseDown;
      } else {
        el.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        el.style.top = `${el.offsetTop - pos2}px`;
        el.style.left = `${el.offsetLeft - pos1}px`;
      }

      function closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
        localStorage.setItem('aceImportTop', el.style.top);
        localStorage.setItem('aceImportLeft', el.style.left);
      }
    },

    selectClass(index, classValue) {
      const row = this.parsedRows[index];
      if (!row) return;
      row.selectedClass = classValue;
      row.classQuery = classValue;
      row.classMatches = [];
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    },

    async importStatement() {
      if (this.isSubmitDisabled) return;

      try {
        this.isSaving = true;
        this.errorMessage = '';

        const payload = {
          statementMonth: this.statementMonth,
          parent: this.parent,
          children: this.parsedRows.map((row) => ({
            ref: row.ref,
            description: row.description,
            amount: row.amount,
            class: row.selectedClass,
          })),
        };

        await axios.post('/api/ace-statements/import', payload);
        this.handleClose();
      } catch (error) {
        console.error('Error importing statement:', error);
        this.errorMessage =
          error?.response?.data?.error ||
          'Import failed. Please review and try again.';
      } finally {
        this.isSaving = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.ace-import-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ace-import-modal {
  width: 875px;
  max-width: 98vw;
  max-height: 90vh;
  background: white;
  border-radius: 8px;
  border: 1px solid #444;
  display: flex;
  flex-direction: column;
  position: absolute;
}

.ace-import-modal.html-open {
  width: 1600px;
}

.ace-import-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #333;
  color: white;
}

.draggable-header {
  cursor: move;
  user-select: none;
}

.ace-import-header h3 {
  margin: 0;
  font-size: 13pt;
}

.close-btn {
  border: 1px solid #888;
  background: #4b0082;
  color: white;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
}

.busy-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 8px;
}

.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid #d8c5e2;
  border-top-color: #4b0082;
  animation: spin 0.8s linear infinite;
}

.busy-label {
  font-size: 10pt;
  color: #4b0082;
  font-weight: 600;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ace-import-body {
  padding: 10px;
  overflow: hidden;
  display: flex;
  gap: 8px;
}

.ace-import-main {
  flex: 0 0 824px;
  width: 824px;
  max-width: 824px;
  min-width: 824px;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ace-import-main::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.section-header {
  font-weight: 600;
  margin: 10px 0 8px;
}

.file-instructions {
  font-size: 8.5pt;
  color: #999;
  margin-bottom: 6px;
}

.file-list {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.file-btn {
  border: 1px solid #ccc;
  background: #f2f2f2;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.file-btn.selected {
  background: #4b0082;
  color: white;
  border-color: #4b0082;
}

.parent-form {
  border: 1px solid #ddd;
  padding: 8px;
  border-radius: 6px;
  margin-bottom: 10px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 9pt;
}

input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px;
}

.full-row {
  margin-top: 8px;
}

.rows-table-wrap {
  border: 1px solid #ddd;
  border-radius: 6px;
  max-height: 44vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.rows-table-wrap::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.rows-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9pt;
  table-layout: fixed;
}

.rows-table .col-ref {
  width: 72px;
}

.rows-table .col-description {
  width: 180px;
}

.rows-table .col-amount {
  width: 110px;
}

.rows-table .col-class {
  width: auto;
}

.rows-table th,
.rows-table td {
  border-bottom: 1px solid #ececec;
  padding: 3px 6px;
  vertical-align: top;
  word-break: break-word;
}

.rows-table th {
  text-align: left;
}

.rows-table th.amount-col,
.rows-table td.amount-col {
  text-align: right;
}

.rows-table tfoot td {
  background: #f0f0f0;
  font-weight: 700;
  border-top: 1px solid #d0d0d0;
}

.rows-table tfoot .total-label {
  text-align: left;
}

.class-cell {
  min-width: 180px;
  position: relative;
}

.class-input {
  width: min(100%, 360px);
  max-width: 360px;
  padding: 3px 6px;
}

.dropdown {
  position: absolute;
  left: 6px;
  width: min(calc(100% - 12px), 360px);
  top: 34px;
  background: #181818b5;
  border-top: 1px solid #bbb;
  border-left: 1px solid #bbb;
  max-height: 220px;
  overflow-y: auto;
  z-index: 50;
  color: white;
}

.dropdown-item {
  padding: 5px;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f0f0f0;
  color: #333;
}

.warning {
  margin: 8px 0;
  background: #fff1cc;
  border: 1px solid #e6c86a;
  color: #7a5a00;
  padding: 8px;
  border-radius: 4px;
}

.error {
  margin-top: 8px;
  color: #b00020;
  font-size: 9pt;
}

.hint {
  color: #777;
  font-size: 9pt;
}

.html-toggle {
  align-self: stretch;
  width: 24px;
  height: 700px;
  border: 1px solid #ddd5d5;
  background: #f3f3f3;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  color: #888;
}

.source-panel {
  flex: 1;
  width: auto;
  min-width: 320px;
  border: 1px solid #ddd;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  background: #fafafa;
}

.source-panel-title {
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  font-weight: 600;
  font-size: 9pt;
  background: #efefef;
}

.source-html {
  width: 100%;
  height: 74vh;
  border: 0;
  max-height: 74vh;
}

.source-empty {
  padding: 8px;
  color: #777;
  font-size: 9pt;
}

.ace-import-footer {
  border-top: 1px solid #ddd;
  padding: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.secondary,
.primary {
  border: 1px solid #999;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.primary {
  background: #4b0082;
  color: white;
  border-color: #4b0082;
}

.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
